import { useState, useEffect } from "react";
import PostCard from "./PostCard";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_POSTS, GET_FEATURED_POST } from "./queries";
import Skeleton from "react-loading-skeleton";

export default function Home() {
  const perPage = 13; // Fetch extra to account for featured post
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

  const { data: postsData, error: postsError } = useQuery(GET_POSTS, {
    variables: { first: perPage },
  });

  const { data: featuredData, error: featuredError } =
    useQuery(GET_FEATURED_POST);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const posts = postsData?.posts?.nodes || [];
  const featuredPost = featuredData?.posts?.nodes[0];

  // Exclude featured post from the grid
  const remainingPosts = posts
    .filter((post: any) => post.id !== featuredPost?.id)
    .slice(0, 12);

  useEffect(() => {
    if (!featuredPost) return;

    const img = new Image();
    const sizes = featuredPost?.featuredImage?.node?.mediaDetails?.sizes;
    const largeUrl = sizes?.find((s: any) => s.name === "large")?.sourceUrl;
    const mediumUrl = sizes?.find((s: any) => s.name === "medium")?.sourceUrl;
    const fallbackUrl = featuredPost?.featuredImage?.node?.sourceUrl;

    const finalImageUrl =
      windowWidth <= 768
        ? mediumUrl || fallbackUrl
        : largeUrl || mediumUrl || fallbackUrl;

    if (finalImageUrl) {
      img.onload = () => setIsImageLoaded(true);
      img.src = finalImageUrl;
    }
  }, [featuredPost, windowWidth]);

  if (postsError || featuredError) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <p className="text-red-600 font-bold">
          Error: {postsError?.message || featuredError?.message}
        </p>
      </div>
    );
  }

  return (
    <>
      <title>NDSU Spectrum Newspaper: For the Land and the People</title>
      <meta
        name="description"
        content="The NDSU Spectrum Newspaper, home to all things NDSU. "
      />

      <div className="bg-white">
        {/* Featured Post Section */}
        <section className="container mx-auto px-4 py-12 md:py-20 border-b border-gray-100">
          <div className="max-w-4xl mx-auto">
            {featuredPost ? (
              <article className="flex flex-col items-center">
                <header className="text-center w-full mb-10">
                  <h1
                    dangerouslySetInnerHTML={{ __html: featuredPost.title }}
                    className="text-4xl md:text-6xl font-heading font-black text-gray-900 leading-tight mb-6"
                  />
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <span className="h-px w-10 bg-[#0A5640]"></span>
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-[0.2em]">
                      Featured Story
                    </p>
                    <span className="h-px w-10 bg-[#0A5640]"></span>
                  </div>
                </header>

                <div className="w-full mb-12 relative group">
                  {isImageLoaded ? (
                    <Link
                      to={`/post/${featuredPost.slug}`}
                      className="block overflow-hidden rounded-xl shadow-xl"
                    >
                      <img
                        src={
                          windowWidth <= 768
                            ? featuredPost.featuredImage.node.mediaDetails.sizes.find(
                                (s: any) => s.name === "medium",
                              )?.sourceUrl
                            : featuredPost.featuredImage.node.mediaDetails.sizes.find(
                                (s: any) => s.name === "large",
                              )?.sourceUrl ||
                              featuredPost.featuredImage.sourceUrl
                        }
                        alt={featuredPost.title}
                        className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
                      />
                    </Link>
                  ) : (
                    <div className="w-full aspect-video">
                      <Skeleton height="100%" className="rounded-xl" />
                    </div>
                  )}
                </div>

                <div className="max-w-3xl w-full">
                  <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <p className="text-sm">
                        <span className="text-gray-400 font-bold uppercase tracking-wider text-xs block mb-1">
                          Written By
                        </span>
                        <Link
                          to={`/author/${featuredPost.author?.node?.slug}`}
                          className="text-[#0A5640] font-black hover:underline text-lg"
                        >
                          {featuredPost.author?.node?.name}
                        </Link>
                      </p>
                    </div>
                    <Link
                      to={`/post/${featuredPost.slug}`}
                      className="bg-[#0A5640] text-white px-6 py-2 rounded-lg font-bold hover:bg-[#084533] transition-all shadow-md hover:shadow-lg"
                    >
                      Read Story
                    </Link>
                  </div>

                  <div
                    className="prose prose-lg md:prose-xl max-w-none text-gray-700 font-body leading-relaxed line-clamp-4 md:line-clamp-6 mb-8"
                    dangerouslySetInnerHTML={{ __html: featuredPost.content }}
                  />
                </div>
              </article>
            ) : (
              <div className="flex flex-col items-center">
                <Skeleton width="80%" height={60} className="mb-6" />
                <Skeleton
                  width="100%"
                  height={400}
                  className="rounded-xl mb-10"
                />
                <Skeleton count={4} width="100%" />
              </div>
            )}
          </div>
        </section>

        {/* Articles List Section */}
        <section className="bg-gray-50 py-20">
          <div className="container mx-auto px-4">
            <header className="flex items-center justify-between mb-12 max-w-7xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-heading font-black text-gray-900 tracking-tight">
                Recent News
              </h2>
              <div className="h-px flex-grow mx-8 bg-gray-200 hidden md:block"></div>
              <Link
                to="/articles"
                className="text-[#0A5640] font-black uppercase tracking-widest text-sm hover:translate-x-1 transition-transform inline-flex items-center gap-2"
              >
                All Articles <span>→</span>
              </Link>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-7xl mx-auto mb-16">
              {remainingPosts.length > 0
                ? remainingPosts.map((post: any) => (
                    <Link
                      key={post.id}
                      to={`/post/${post.slug}`}
                      className="group flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl hover:border-green-100 transition-all duration-300"
                    >
                      <div className="p-6 flex-grow">
                        <PostCard post={post} />
                      </div>
                    </Link>
                  ))
                : [...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
                    >
                      <Skeleton height={200} className="rounded-lg mb-4" />
                      <Skeleton count={3} />
                    </div>
                  ))}
            </div>

            <div className="text-center">
              <Link to={`/articles`} className="inline-block">
                <button className="bg-white text-[#0A5640] border-2 border-[#0A5640] px-10 py-4 rounded-xl font-black uppercase tracking-widest text-sm transition-all duration-300 hover:bg-[#0A5640] hover:text-white shadow-sm hover:shadow-lg">
                  Load More News
                </button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
